import React from 'react';
import {CodeResponse, NonOAuthError, useGoogleLogin} from '@react-oauth/google';
import {Button} from '@forecast-it/design-system';
import {trackEvent} from '../../../../tracking/amplitude/TrackingV2';
import {TRACKING_OBJECTS} from '../../../../tracking/amplitude/constants/TrackingObjects';

type GoogleCalendarOauthButtonProps = {
	title: string;
	onSuccess: (codeResponse: Omit<CodeResponse, 'error' | 'error_description' | 'error_uri'>) => void;
	onError: (errorResponse: Pick<CodeResponse, 'error' | 'error_description' | 'error_uri'>) => void;
	onNonOAuthError: (nonOAuthError: NonOAuthError) => void;
	calendarTokenError?: string;
};

const GoogleCalendarOauthButton = ({
	title,
	onSuccess,
	onError,
	onNonOAuthError,
	calendarTokenError,
}: GoogleCalendarOauthButtonProps): JSX.Element => {
	// read-only scope for Google calendar api
	const calendarScopeRO = 'https://www.googleapis.com/auth/calendar.readonly';
	// read-only scope for Google calendar list api
	const calendarListScopeRO = 'https://www.googleapis.com/auth/calendar.calendarlist.readonly';

	const validateTokenResponse = tokenResponse => {
		// Verify that user accepted both requested scopes
		return !!(tokenResponse?.scope?.includes(calendarScopeRO) && tokenResponse?.scope?.includes(calendarListScopeRO));
	};

	const handleSuccess = async (tokenResponse: Omit<CodeResponse, 'error' | 'error_description' | 'error_uri'>) => {
		if (validateTokenResponse(tokenResponse)) {
			return onSuccess(tokenResponse);
		} else {
			return onError({error_description: 'We did not receive the necessary permissions to access your calendar.'});
		}
	};

	const googleLogin = useGoogleLogin({
		onSuccess: handleSuccess,
		onError: onError,
		onNonOAuthError: onNonOAuthError,
		scope: `${calendarScopeRO} ${calendarListScopeRO}`,
		flow: 'auth-code',
	});

	const handleLinkClick = () => {
		trackEvent(TRACKING_OBJECTS.LINK_GOOGLE_CALENDAR_ACCOUNT, 'Clicked');
		googleLogin();
	};
	return (
		<Button title={title} emphasis={'medium'} onPress={() => handleLinkClick()}>
			{calendarTokenError ? 'Re-authenticate' : 'Link Google Account'}
		</Button>
	);
};

export default GoogleCalendarOauthButton;
